import React, { createContext, useContext, useState, ReactNode } from 'react';

interface EventValidationContextType {
  fieldErrors: Record<string, string>;
  setFieldError: (field: string, error: string) => void;
  clearFieldError: (field: string) => void;
  hasEventValidationErrors: () => boolean;
  // Enable/disable functionality
  isFormEnabled: boolean;
  toggleFormEnabled: () => void;
  isFieldEnabled: (baseDisabled: boolean) => boolean;
}

const EventValidationContext = createContext<EventValidationContextType | undefined>(undefined);

export const EventValidationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isFormEnabled, setIsFormEnabled] = useState(false);

  const setFieldError = (field: string, error: string) => {
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const clearFieldError = (field: string) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const hasEventValidationErrors = () => Object.keys(fieldErrors).length > 0;

  const toggleFormEnabled = () => {
    setIsFormEnabled(prev => !prev);
  };

  const isFieldEnabled = (baseDisabled: boolean) => {
    // If the form is not enabled, all fields are disabled
    // If the form is enabled, use the base disabled state
    return isFormEnabled && !baseDisabled;
  };

  return (
    <EventValidationContext.Provider value={{ 
      fieldErrors, 
      setFieldError, 
      clearFieldError, 
      hasEventValidationErrors,
      isFormEnabled,
      toggleFormEnabled,
      isFieldEnabled
    }}>
      {children}
    </EventValidationContext.Provider>
  );
};

export const useEventValidation = () => {
  const context = useContext(EventValidationContext);
  if (!context) {
    throw new Error('useEventValidation must be used within EventValidationProvider');
  }
  return context;
}; 